import { ActivatedRouteSnapshot } from '@angular/router';

import { ISvcChipOption } from '@solvace/component/svc-controls';

import {
  SvcModalActionPlanNotificationParams,
  SvcModalActionPlanSystemData,
  SvcModalActionPlanSystemReference,
} from '../interfaces/svc-modal-action-plan.interface';
import { ObeyaReferenceType, isObeyaApplicationId } from './actionplan-obeya-reference.utils';

/**
 * Reads `applicationId`, `referenceValue` and `referenceTypeId` and builds a
 * `SvcModalActionPlanNotificationParams` payload. Returns `undefined` when
 * no recognised param is present so callers can short-circuit the
 * widget-creation branch without checking each field.
 *
 * `referenceValue` and `referenceTypeId` are REPEATABLE: a URL with
 * `?referenceValue=1&referenceTypeId=8&referenceValue=2&referenceTypeId=9`
 * yields two system references, paired by index, sharing the single
 * `applicationId`. The first pair is also mirrored on the flat
 * `referenceValue` / `referenceTypeId` for backward-compatible callers.
 * Legacy single-name aliases (`applicationReferenceId` /
 * `applicationAdditionalReferenceId`) are still honoured as a fallback.
 *
 * Two sources, in order:
 *  1. The route's `queryParamMap` — works for in-app routing and unit tests.
 *  2. `globalThis.location.search` — the iframe's own URL. Inside the
 *     notification iframe the route snapshot can resolve before the query
 *     string is attached to the route tree, so reading the route alone loses
 *     the params. RCA's type-component reads the iframe `src` for exactly
 *     this reason; here we read the iframe's `location.search`, which is the
 *     same URL the legacy set via `iframe.src`.
 */
export function extractNotificationParamsFromRoute(
  route: ActivatedRouteSnapshot,
): SvcModalActionPlanNotificationParams | undefined {
  const queryParamMap = route?.queryParamMap;
  const fromRoute = buildNotificationParams(
    queryParamMap?.get('applicationId') ?? null,
    readAllParams(queryParamMap, 'referenceValue', 'applicationReferenceId'),
    readAllParams(queryParamMap, 'referenceTypeId', 'applicationAdditionalReferenceId'),
  );
  if (fromRoute) return fromRoute;

  const search = new URLSearchParams(globalThis.location?.search ?? '');
  return buildNotificationParams(
    search.get('applicationId'),
    readAllParams(search, 'referenceValue', 'applicationReferenceId'),
    readAllParams(search, 'referenceTypeId', 'applicationAdditionalReferenceId'),
  );
}

/**
 * Reads the `isDraft` flag from the route/iframe URL, mirroring
 * {@link extractNotificationParamsFromRoute}'s dual-source lookup (route
 * `queryParamMap` first, then the iframe's `location.search`). Defaults to
 * `false` when absent; accepts `true`/`1` (case-insensitive) as truthy.
 */
export function extractIsDraftFromRoute(route: ActivatedRouteSnapshot): boolean {
  const search = new URLSearchParams(globalThis.location?.search ?? '');
  const readParam = (key: string): string | null => route?.queryParamMap?.get(key) ?? search.get(key);
  const raw = readParam('isDraft') ?? readParam('includeDraft');
  const normalized = (raw ?? '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1';
}

interface ReadableQueryParams {
  getAll(name: string): string[];
}

/**
 * Reads every value for `key` (repeatable query param), falling back to the
 * legacy single-name `legacyKey` only when `key` is absent. Works for both
 * Angular's `ParamMap` and `URLSearchParams` (both expose `getAll`).
 */
function readAllParams(
  params: ReadableQueryParams | null | undefined,
  key: string,
  legacyKey: string,
): string[] {
  const values = params?.getAll?.(key) ?? [];
  if (values.length) return values;
  return params?.getAll?.(legacyKey) ?? [];
}

function buildNotificationParams(
  rawApplicationId: string | null,
  rawReferenceValues: readonly string[],
  rawReferenceTypeIds: readonly string[],
): SvcModalActionPlanNotificationParams | undefined {
  const applicationId = normalizeOptionalString(rawApplicationId);
  const systemReferences = buildSystemReferences(applicationId, rawReferenceValues, rawReferenceTypeIds);

  const hasAnyParam = !!applicationId || systemReferences.length > 0;
  if (!hasAnyParam) return undefined;

  const [firstReference] = systemReferences;

  return {
    applicationId,
    referenceValue: firstReference?.referenceValue,
    referenceTypeId: firstReference?.referenceTypeId,
    ...(systemReferences.length ? { systemReferences } : {}),
  };
}

/**
 * Pairs `referenceValue[i]` with `referenceTypeId[i]` into one system
 * reference each, repeating the shared `applicationId`. Entries with neither
 * a valid value nor a valid type are dropped; uneven counts keep whichever
 * side is present at that index.
 */
function buildSystemReferences(
  applicationId: string | undefined,
  rawReferenceValues: readonly string[],
  rawReferenceTypeIds: readonly string[],
): SvcModalActionPlanSystemReference[] {
  const length = Math.max(rawReferenceValues.length, rawReferenceTypeIds.length);
  const references: SvcModalActionPlanSystemReference[] = [];

  for (let index = 0; index < length; index++) {
    const referenceValue = parsePositiveNumber(rawReferenceValues[index] ?? null);
    const referenceTypeId = parsePositiveNumber(rawReferenceTypeIds[index] ?? null);
    if (referenceValue === undefined && referenceTypeId === undefined) continue;

    references.push({
      ...(applicationId ? { applicationId } : {}),
      ...(referenceValue !== undefined ? { referenceValue } : {}),
      ...(referenceTypeId !== undefined ? { referenceTypeId } : {}),
    });
  }

  return references;
}

/**
 * Locates the system option whose backend GUID (`data.systemId`) matches
 * the `applicationId` sent by the widget caller. Case-insensitive because
 * the CORE team typically sends GUIDs in lowercase while the backend may
 * persist them mixed.
 *
 * Returns `null` when no system can be resolved — callers should leave the
 * `Sistema` control open in that case rather than crash. A missing match
 * is a degraded UX, not a fatal error.
 */
export function findSystemByApplicationId(
  systems: Array<ISvcChipOption<SvcModalActionPlanSystemData>> | null | undefined,
  applicationId: string | null | undefined,
): ISvcChipOption<SvcModalActionPlanSystemData> | null {
  const normalizedApplicationId = (applicationId ?? '').trim().toLowerCase();
  if (!normalizedApplicationId) return null;

  return (
    (systems ?? []).find((option) => {
      const systemGuid = (option?.data?.systemId ?? '').trim().toLowerCase();
      return !!systemGuid && systemGuid === normalizedApplicationId;
    }) ?? null
  );
}

/**
 * Source label to display when the action plan is being created from an
 * external widget (draft ACP, before the definitive parent record exists).
 *
 * For Digital Obeya the backend persists the source as `DOB-<roomId>`, so we
 * mirror that exact tagged format here (literal `DOB-` prefix to stay
 * consistent with the value the GET will replace it with). The room id arrives
 * as the type-3 reference (`ObeyaReferenceType.Room`); we fall back to the
 * first reference value when the typed room reference is absent.
 *
 * For every other integration the only value available on a draft is the raw
 * numeric reference id (e.g. `544230101`) We return `null` so the caller keeps its default `'-'` label
 * until the saved record's tagged reference (e.g. `DFT-264`) arrives via the
 * GET. Also returns `null` when there is no reference id at all.
 */
export function resolveWidgetSourceLabel(
  params: SvcModalActionPlanNotificationParams | null | undefined,
): string | null {
  const referenceValue = params?.referenceValue;
  if (referenceValue === null || referenceValue === undefined) return null;

  if (isObeyaApplicationId(params?.applicationId)) {
    const roomReference = (params?.systemReferences ?? []).find(
      (reference) => reference?.referenceTypeId === ObeyaReferenceType.Room,
    );
    return `DOB-${roomReference?.referenceValue ?? referenceValue}`;
  }

  return null;
}

function normalizeOptionalString(value: string | null): string | undefined {
  const trimmed = (value ?? '').trim();
  return trimmed ? trimmed : undefined;
}

function parsePositiveNumber(value: string | null): number | undefined {
  if (value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}
